import type { Feature, Geometry } from "geojson";

/**
 * Recursively updates all coordinates in a GeoJSON geometry using the updater function.
 */
function updateGeometryCoordinates(
  geometry: Geometry,
  updater: (
    coord: [number, number] | [number, number, number],
  ) => [number, number] | [number, number, number],
): Geometry {
  switch (geometry.type) {
    case "Point":
      return {
        ...geometry,
        coordinates: updater(
          geometry.coordinates as [number, number] | [number, number, number],
        ),
      };
    case "MultiPoint":
    case "LineString":
      return {
        ...geometry,
        coordinates: (
          geometry.coordinates as (
            | [number, number]
            | [number, number, number]
          )[]
        ).map(updater),
      };
    case "MultiLineString":
    case "Polygon":
      return {
        ...geometry,
        coordinates: (
          geometry.coordinates as (
            | [number, number]
            | [number, number, number]
          )[][]
        ).map((lineOrRing) => lineOrRing.map(updater)),
      };
    case "MultiPolygon":
      return {
        ...geometry,
        coordinates: (
          geometry.coordinates as (
            | [number, number]
            | [number, number, number]
          )[][][]
        ).map((polygon) => polygon.map((ring) => ring.map(updater))),
      };
    case "GeometryCollection":
      return {
        ...geometry,
        geometries: geometry.geometries.map((g) =>
          updateGeometryCoordinates(g, updater),
        ),
      };
    default:
      return geometry;
  }
}

/**
 * Returns a new Feature with all coordinates updated by the updater function.
 */
export function updateCoordinates(
  feature: Feature,
  updater: (
    coord: [number, number] | [number, number, number],
  ) => [number, number] | [number, number, number],
): Feature {
  return {
    ...feature,
    geometry: updateGeometryCoordinates(feature.geometry, updater),
  };
}
